/**
 * TrustBadges Component
 * Displays Razorpay and security icons to build customer confidence
 * Used near payment buttons and in footer
 */

const TrustBadges = ({ variant = 'inline', showText = true }) => {
  // Razorpay Logo SVG
  const RazorpayLogo = () => (
    <svg viewBox="0 0 120 30" width="80" height="20" fill="currentColor">
      <path d="M16.9 3.1L6.6 26.9h5.4l1.8-4.4h9.1l1.8 4.4h5.4L19.8 3.1h-2.9zm1.4 6.1l3 7.5h-6l3-7.5z"/>
      <path d="M36.3 3.1h-5.2v23.8h5.2V3.1zm19.3 0l-6.1 9.8-6.1-9.8h-6l9.1 13.2v10.6h5.2V16.3l9.1-13.2h-5.2zm8.6 0h5.2v23.8h-5.2V3.1zm22.4 0h-5.2v14.5L71.7 3.1h-4.4v23.8h5.2V12.4l9.7 14.5h4.4V3.1zm14.4 0h5.2v23.8h-5.2V3.1z"/>
    </svg>
  );

  // Shield/Security Icon SVG
  const ShieldIcon = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <path d="M9 12l2 2 4-4"/>
    </svg>
  );

  // Lock Icon SVG
  const LockIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );

  // PCI DSS Icon
  const PCIIcon = () => (
    <svg viewBox="0 0 40 20" width="40" height="20" fill="currentColor">
      <rect width="40" height="20" rx="2" fill="currentColor" opacity="0.1"/>
      <text x="20" y="14" textAnchor="middle" fontSize="8" fontWeight="600" fill="currentColor">PCI</text>
    </svg>
  );

  // Inline variant - compact, for near payment buttons (mobile responsive)
  if (variant === 'inline') {
    return (
      <div 
        className="trust-badges-inline" 
        data-testid="trust-badges-inline"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: '8px',
          marginTop: '12px',
          padding: '10px 12px',
          background: 'rgba(76, 175, 80, 0.06)',
          borderRadius: '6px',
          border: '1px solid rgba(76, 175, 80, 0.15)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#4CAF50', padding: '4px 8px', background: 'rgba(76, 175, 80, 0.08)', borderRadius: '4px' }}>
          <LockIcon />
          <span style={{ fontSize: '0.65rem', fontWeight: '500', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>Secure</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#2D5BE3', padding: '4px 8px', background: 'rgba(45, 91, 227, 0.08)', borderRadius: '4px' }} data-testid="razorpay-badge">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M3.31 15.29L6.32 7h2.49l-3.01 8.29h8.2l3.01-8.29h2.49l-3.01 8.29h4.2v2.42h-5.26l-1.05 2.87h-2.49l1.05-2.87H5.69l-1.05 2.87H2.15l1.16-3.2v-1.8z"/>
          </svg>
          <span style={{ fontSize: '0.65rem', fontWeight: '600', whiteSpace: 'nowrap' }}>Razorpay</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#8B4513', padding: '4px 8px', background: 'rgba(139, 69, 19, 0.08)', borderRadius: '4px' }}>
          <ShieldIcon />
          <span style={{ fontSize: '0.65rem', fontWeight: '500', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>Protected</span>
        </div>
      </div>
    );
  }

  // Compact variant - minimal, for tight spaces
  if (variant === 'compact') {
    return (
      <div 
        className="trust-badges-compact" 
        data-testid="trust-badges-compact"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          marginTop: '8px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#4CAF50' }}>
          <LockIcon />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#2D5BE3' }}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M3.31 15.29L6.32 7h2.49l-3.01 8.29h8.2l3.01-8.29h2.49l-3.01 8.29h4.2v2.42h-5.26l-1.05 2.87h-2.49l1.05-2.87H5.69l-1.05 2.87H2.15l1.16-3.2v-1.8z"/>
          </svg>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#8B4513' }}>
          <ShieldIcon />
        </div>
      </div>
    );
  }

  // Footer variant - full width, more detailed (mobile responsive)
  if (variant === 'footer') {
    return (
      <div 
        className="trust-badges-footer" 
        data-testid="trust-badges-footer"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '12px',
          marginTop: '20px',
          paddingTop: '16px',
          borderTop: '1px solid rgba(212, 168, 67, 0.2)'
        }}
      >
        <span style={{ 
          fontSize: '0.65rem', 
          letterSpacing: '0.15em', 
          textTransform: 'uppercase', 
          color: '#D4A843',
          fontWeight: '500'
        }}>
          Secure Payments
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {/* Razorpay Badge */}
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px',
              padding: '5px 10px',
              background: 'rgba(45, 91, 227, 0.08)',
              borderRadius: '4px',
              border: '1px solid rgba(45, 91, 227, 0.2)'
            }}
            data-testid="footer-razorpay-badge"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="#2D5BE3">
              <path d="M3.31 15.29L6.32 7h2.49l-3.01 8.29h8.2l3.01-8.29h2.49l-3.01 8.29h4.2v2.42h-5.26l-1.05 2.87h-2.49l1.05-2.87H5.69l-1.05 2.87H2.15l1.16-3.2v-1.8z"/>
            </svg>
            <span style={{ fontSize: '0.65rem', fontWeight: '600', color: '#2D5BE3', whiteSpace: 'nowrap' }}>Razorpay</span>
          </div>
          
          {/* SSL Secure Badge */}
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px',
              padding: '5px 10px',
              background: 'rgba(76, 175, 80, 0.08)',
              borderRadius: '4px',
              border: '1px solid rgba(76, 175, 80, 0.2)'
            }}
            data-testid="footer-ssl-badge"
          >
            <LockIcon />
            <span style={{ fontSize: '0.65rem', fontWeight: '500', color: '#4CAF50', whiteSpace: 'nowrap' }}>SSL Encrypted</span>
          </div>
          
          {/* 100% Secure Badge */}
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px',
              padding: '5px 10px',
              background: 'rgba(212, 168, 67, 0.08)',
              borderRadius: '4px',
              border: '1px solid rgba(212, 168, 67, 0.2)'
            }}
            data-testid="footer-secure-badge"
          >
            <ShieldIcon />
            <span style={{ fontSize: '0.65rem', fontWeight: '500', color: '#D4A843', whiteSpace: 'nowrap' }}>100% Secure</span>
          </div>
        </div>
      </div>
    );
  }

  // Default: return inline variant
  return null;
};

export default TrustBadges;
